import { useMemo, useState } from 'react';
import { cn } from '../../lib/utils';
import { useTopAgentsVolume } from '../../hooks';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const PERIODS = ['7D', '14D', '30D'];
const barColors = ['bg-[#721b1f]', 'bg-[#30abe8]', 'bg-[#22C35D]', 'bg-[#b152e0]', 'bg-[#f59f0a]', 'bg-[#818898]'];

function getRangeForPeriod(period) {
  const today = new Date();
  const startDate = new Date();
  startDate.setDate(today.getDate() - parseInt(period.slice(0, -1), 10));
  const formatDate = date => date.toISOString().split('T')[0];
  return { start_date: formatDate(startDate), end_date: formatDate(today) };
}

export function TopAgents({ enabled = true }) {
  const [activePeriod, setActivePeriod] = useState('7D');
  const { start_date, end_date } = getRangeForPeriod(activePeriod);
  const { data: topAgentsData, isPending } = useTopAgentsVolume(start_date, end_date, { enabled });

  const { totalRequests, totalRequestsOthers } = useMemo(() => {
    const entities = topAgentsData?.top_entities || [];
    const total = entities.reduce((sum, agent) => sum + (agent?.count || 0), 0);
    const others = entities.slice(5).reduce((sum, agent) => sum + (agent?.count || 0), 0);
    return { totalRequests: total, totalRequestsOthers: others };
  }, [topAgentsData?.top_entities]);

  return (
    <div className="p-6 rounded-xl bg-white border border-shell-border">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-ink">Top Agents</h3>
          <p className="text-xs text-mute mt-0.5">By request volume</p>
        </div>
        <div className="flex gap-1">
          {PERIODS.map(period => (
            <button
              key={period}
              type="button"
              onClick={() => setActivePeriod(period)}
              className={cn(
                'text-xs px-2.5 py-1 rounded-md transition-colors',
                period === activePeriod
                  ? 'bg-primary text-white font-semibold'
                  : 'text-mute hover:text-ink hover:bg-paper',
              )}>
              {period}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {isPending ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={`skeleton-${i}`}>
              <Skeleton height={20} borderRadius={6} baseColor="#262626" highlightColor="#333" />
            </div>
          ))
        ) : (
          <>
            {topAgentsData?.top_entities?.slice(0, 5)?.map((agent, i) => (
              <div key={agent?.name || i}>
                <div className="flex items-center justify-between mb-1.5 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {agent?.icon ? (
                      <img src={agent.icon} alt="" className="size-6 rounded-full flex-shrink-0" />
                    ) : (
                      <div className="size-6 rounded-full bg-primary/10 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium text-ink truncate">{agent?.name}</span>
                    <span className="text-xs capitalize text-mute border border-shell-border rounded-full px-2 py-0.5 flex-shrink-0">
                      {agent?.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-mute">{agent?.count}</span>
                    <span className="text-xs font-medium text-ink w-10 text-right">
                      {totalRequests ? ((agent?.count / totalRequests) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </div>
                <div className="w-full bg-[#262626] rounded-full h-1.5">
                  <div
                    className={cn('h-1.5 rounded-full transition-all duration-500', barColors[i])}
                    style={{ width: totalRequests ? `${(agent?.count / totalRequests) * 100}%` : '0%' }}
                  />
                </div>
              </div>
            ))}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-ink">Others</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-mute">{totalRequestsOthers}</span>
                  <span className="text-xs font-medium text-ink w-10 text-right">
                    {totalRequests ? ((totalRequestsOthers / totalRequests) * 100).toFixed(1) : 0}%
                  </span>
                </div>
              </div>
              <div className="w-full bg-[#262626] rounded-full h-1.5">
                <div
                  className={cn('h-1.5 rounded-full transition-all duration-500', barColors[5])}
                  style={{
                    width: totalRequests ? `${(totalRequestsOthers / totalRequests) * 100}%` : '0%',
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
